import * as React from 'react';
import { GripVertical, Lock } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import { Slot } from '@radix-ui/react-slot';
import {
  dragHandleRecipe,
  dragLockRecipe,
  paneBodyRecipe,
  paneHeaderRecipe,
  paneRecipe,
  railRowBarRecipe,
  railRowRecipe,
  railRowTileRecipe,
  studioBodyRecipe,
  studioFooterRecipe,
  studioFrameRecipe,
  studioHeaderRecipe,
  studioPaneRecipe,
  type DragHandleRecipeProps,
  type PaneBodyRecipeProps,
  type PaneHeaderRecipeProps,
  type RailRowRecipeProps,
  type StudioFrameRecipeProps,
} from '../recipes/studio.recipe';
import { uiMarkerInternal } from '../recipes/shared';
import { Icon } from './Icon';
import { Skeleton } from './Skeleton';
import { Text } from './Text';

export interface StudioLayoutProps
  extends React.HTMLAttributes<HTMLDivElement>,
    StudioFrameRecipeProps {
  /** The top bar's contents. Two children sit at either end. */
  header?: React.ReactNode;
  /** The left pane: the list of pages. Hidden below `lg`. */
  rail?: React.ReactNode;
  /** The middle pane: the live preview. Always shown. */
  stage?: React.ReactNode;
  /** The right pane: the editor for the selected page. Hidden below `lg`. */
  inspector?: React.ReactNode;
  /** A strip under the panes, shown only below `lg`. */
  footer?: React.ReactNode;
  /**
   * What fills the frame when there are no panes — the desktop-only gate
   * renders its notice here. With panes, anything else the screen mounts:
   * the modals the header's buttons open.
   */
  children?: React.ReactNode;
}

/**
 * The website studio's frame: a viewport-high card with a top bar and three
 * panes — rail | stage | inspector — on the same column widths at every
 * breakpoint.
 *
 *   <StudioLayout header={<>…</>} rail={<PagesRail />} stage={<PreviewStage />}
 *     inspector={<Inspector />} />
 *
 * The editor, its loading skeleton and the booking editor all drew this grid
 * by hand with the same two `grid-cols-[…]` strings, which is the whole
 * reason to give it a name: they were meant to line up, and now they cannot
 * do otherwise. `height="tall"` is the skeleton's, which sits under no page
 * header.
 */
export const StudioLayout = React.forwardRef<HTMLDivElement, StudioLayoutProps>(
  function StudioLayout(
    {
      header,
      rail,
      stage,
      inspector,
      footer,
      height,
      className,
      children,
      ...props
    },
    ref
  ) {
    const hasPanes =
      rail !== undefined || stage !== undefined || inspector !== undefined;
    return (
      <div
        ref={ref}
        className={cn(studioFrameRecipe({ height }), className)}
        {...props}
      >
        {/* A div rather than a <header>: the frame sits inside the page's own
            banner-bearing shell, and a second landmark would only confuse. */}
        {header !== undefined ? (
          <div className={studioHeaderRecipe()}>{header}</div>
        ) : null}
        {hasPanes ? (
          <div className={studioBodyRecipe()}>
            <div className={studioPaneRecipe({ pane: 'rail' })}>{rail}</div>
            <div className={studioPaneRecipe({ pane: 'stage' })}>{stage}</div>
            <div className={studioPaneRecipe({ pane: 'inspector' })}>
              {inspector}
            </div>
          </div>
        ) : null}
        {children}
        {footer !== undefined ? (
          <div className={studioFooterRecipe()}>{footer}</div>
        ) : null}
      </div>
    );
  }
);

export interface PaneProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Hand the column's classes to the child instead of rendering a div — for
   * a framer `motion.div`, which is how the skeleton fades its panes in.
   */
  asChild?: boolean;
}

/**
 * The column inside one of StudioLayout's slots.
 *
 *   <Pane>
 *     <PaneHeader divided>…</PaneHeader>
 *     <PaneBody pane="inspector">…</PaneBody>
 *   </Pane>
 *
 * Takes the pane's full height so the body can scroll under a header that
 * stays put.
 */
export const Pane = React.forwardRef<HTMLDivElement, PaneProps>(function Pane(
  { asChild = false, className, ...props },
  ref
) {
  const Component = asChild ? Slot : 'div';
  return (
    <Component ref={ref} className={cn(paneRecipe(), className)} {...props} />
  );
});

export interface PaneHeaderProps
  extends React.HTMLAttributes<HTMLDivElement>,
    PaneHeaderRecipeProps {}

/** The fixed-height bar across the top of a Pane. */
export const PaneHeader = React.forwardRef<HTMLDivElement, PaneHeaderProps>(
  function PaneHeader({ size, justify, divided, className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(paneHeaderRecipe({ size, justify, divided }), className)}
        {...props}
      />
    );
  }
);

export interface PaneBodyProps
  extends React.HTMLAttributes<HTMLDivElement>,
    PaneBodyRecipeProps {}

/** The part of a Pane under its header: scrolls, or clips for the stage. */
export const PaneBody = React.forwardRef<HTMLDivElement, PaneBodyProps>(
  function PaneBody({ pane, className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(paneBodyRecipe({ pane }), className)}
        {...props}
      />
    );
  }
);

export interface DragHandleProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    DragHandleRecipeProps {
  /** A row that cannot move shows a lock instead of a grip. */
  locked?: boolean;
}

/**
 * The grip that starts a reorder.
 *
 *   <DragHandle aria-label="Drag to reorder"
 *     onPointerDown={(e) => { e.stopPropagation(); controls.start(e); }} />
 *
 * Draws the glyph and the cursor; the caller wires the pointer event to
 * whichever drag system moves the row. `reveal` hides it until the row —
 * which needs the `group` class — is hovered.
 */
export const DragHandle = React.forwardRef<HTMLSpanElement, DragHandleProps>(
  function DragHandle({ locked, size, reveal, className, ...props }, ref) {
    if (locked) {
      return (
        <span ref={ref} className={cn(dragLockRecipe(), className)} {...props}>
          <Icon icon={Lock} size="sm" />
        </span>
      );
    }
    return (
      <span
        ref={ref}
        className={cn(dragHandleRecipe({ size, reveal }), className)}
        {...props}
      >
        <Icon icon={GripVertical} size="sm" />
      </span>
    );
  }
);

export interface RailRowProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'title'>,
    Omit<RailRowRecipeProps, 'interactive'> {
  /** A button when the row is the thing you click; a div when it holds controls. */
  as?: 'div' | 'button';
  /** The glyph in the tile. */
  icon?: LucideIcon;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  /** Beside the label — a "Draft" badge. */
  badge?: React.ReactNode;
  /** After the text — the hide button, the settings popover. */
  trailing?: React.ReactNode;
  /**
   * Draws a drag handle before the tile and hands it these props — the
   * pointer handler and label the drag system needs.
   */
  dragHandleProps?: React.HTMLAttributes<HTMLSpanElement>;
  /** Draws a lock where the handle would be: this row stays put. */
  locked?: boolean;
  /** Draw the row's shape out of Skeleton blocks instead of content. */
  loading?: boolean;
}

/**
 * One page in the studio's rail.
 *
 *   <RailRow icon={Home} label="Home" hint="First impression"
 *     selected={selection === 'hero'} onClick={() => select('hero')}
 *     trailing={<Button size="icon-xs" …/>} />
 *
 * A tile, a name over a hint, and whatever acts on the row at the right.
 * The studio rail, its skeleton and the booking editor's panel list each
 * wrote this row out; the selected treatment — tinted gradient, a bar on
 * the left edge, a filled tile — is the same in all three.
 */
export const RailRow = React.forwardRef<HTMLElement, RailRowProps>(
  function RailRow(
    {
      as = 'div',
      icon,
      label,
      hint,
      badge,
      trailing,
      dragHandleProps,
      locked,
      selected = false,
      dimmed,
      loading = false,
      className,
      children,
      ...props
    },
    ref
  ) {
    const interactive = as === 'button' || props.onClick !== undefined;

    const content = (
      <>
        {selected ? <span className={railRowBarRecipe({ loading })} /> : null}
        {locked ? (
          <DragHandle locked />
        ) : dragHandleProps ? (
          <DragHandle reveal {...dragHandleProps} />
        ) : null}
        {loading ? (
          <Skeleton
            shape="tile"
            tone={selected ? 'primary' : 'default'}
            className="size-9"
          />
        ) : icon ? (
          <span className={railRowTileRecipe({ selected })}>
            <Icon icon={icon} className="size-[18px]" />
          </span>
        ) : null}
        <div className={`${uiMarkerInternal} flex min-w-0 flex-1 flex-col`}>
          {loading ? (
            <div className={`${uiMarkerInternal} flex flex-col gap-2`}>
              <Skeleton className="h-3 w-24" />
              <Skeleton tone="subtle" className="h-2.5 w-32" />
            </div>
          ) : (
            <>
              <span className={`${uiMarkerInternal} flex items-center gap-2`}>
                <Text
                  as="span"
                  variant="body-sm-strong"
                  className="leading-tight"
                >
                  {label}
                </Text>
                {badge}
              </span>
              {hint !== undefined ? (
                <Text as="span" variant="caption" tone="muted" truncate>
                  {hint}
                </Text>
              ) : null}
            </>
          )}
        </div>
        {trailing}
        {children}
      </>
    );

    if (as === 'button') {
      const buttonRef = ref as React.Ref<HTMLButtonElement>;
      const buttonProps =
        props as React.ButtonHTMLAttributes<HTMLButtonElement>;
      return (
        <button
          ref={buttonRef}
          type="button"
          className={cn(
            railRowRecipe({ selected, interactive, loading, dimmed }),
            className
          )}
          {...buttonProps}
        >
          {content}
        </button>
      );
    }
    // Cast outside the tag: a generic's `>` inside the attributes is where the
    // plain-div overlay test stops reading them.
    const divRef = ref as React.Ref<HTMLDivElement>;
    const divProps = props as React.HTMLAttributes<HTMLDivElement>;
    return (
      <div
        ref={divRef}
        className={cn(
          railRowRecipe({ selected, interactive, loading, dimmed }),
          className
        )}
        {...divProps}
      >
        {content}
      </div>
    );
  }
);
